import * as React from "react";

import AppModals from "@editor/components/AppModals";
import ManageChats from "@editor/components/editor/ai/chat/ManageChats";
import { DebugModal } from "@editor/components/editor/debug/DebugPanel";
import { useReploHotkeys } from "@editor/hooks/useHotkeys";
import { useEditorDispatch, useEditorSelector } from "@editor/store";
import { routes } from "@editor/utils/router";
import {
  selectInternalDebugModeOn,
  setDebugPanelVisibility,
} from "@reducers/core-reducer";

import { useChatSidebar } from "@/features/react-editor/hooks/useChatSidebar";
import { ToastManager } from "@replo/design-system/components/alert/Toast";
import { MobileSheet } from "@replo/design-system/components/mobile-sheet/MobileSheet";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "@replo/design-system/components/shadcn/core/sidebar";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { matchPath, Outlet, useLocation } from "react-router-dom";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

import DashboardSidebar from "./DashboardSidebar";
import MobileChatHeader from "./MobileChatHeader";

function ChatRouteSidebarListener() {
  const { pathname } = useLocation();
  const { open, openMobile, toggleSidebar } = useSidebar();
  const isMobile = useIsMobile();
  const isExpanded = isMobile ? openMobile : open;

  useOnValueChange(pathname, (newPath, oldPath) => {
    const wasChat = Boolean(
      oldPath && matchPath("/shops/:projectId/chat/*", oldPath),
    );
    const isChat = Boolean(
      newPath && matchPath("/shops/:projectId/chat/*", newPath),
    );
    if (!wasChat && isChat && isExpanded) {
      toggleSidebar();
    }
  });

  return null;
}

const DashboardLayout: React.FC<
  React.PropsWithChildren<{ contentIsCard: boolean }>
> = ({ children, contentIsCard }) => {
  const dispatch = useEditorDispatch();
  const isDebugPanelVisible = useEditorSelector(selectInternalDebugModeOn);
  const toggleDebugMode = React.useCallback(() => {
    dispatch(setDebugPanelVisibility(!isDebugPanelVisible));
  }, [dispatch, isDebugPanelVisible]);
  useReploHotkeys({ debug: toggleDebugMode });
  const { pathname } = useLocation();

  const mobileLayoutRoutes = [
    routes.shops.home,
    routes.shops.products.list,
    routes.shops.products.edit,
    routes.shops.products.new,
    "/shops/:projectId/chat",
  ];

  const isMobileLayoutRoute = mobileLayoutRoutes.some((route) =>
    Boolean(matchPath(route, pathname)),
  );

  const isShopsHome = Boolean(matchPath(routes.shops.home, pathname));
  const { open: isChatSidebarOpen, setOpen: setIsChatSidebarOpen } =
    useChatSidebar();
  const isMobile = useIsMobile();

  return (
    <div
      className={twMerge("flex flex-col", isMobile ? "h-[100dvh]" : "h-screen")}
    >
      <ToastManager />
      <AppModals />
      {isDebugPanelVisible && <DebugModal />}
      <div className="h-screen grow no-scrollbar overflow-y-scroll">
        <SidebarProvider
          style={
            {
              "--sidebar-width": "calc(var(--spacing) * (56 + 7))",
              "--sidebar-width-icon": "calc(var(--spacing) * (16 + 2.5))",
            } as React.CSSProperties
          }
          className="h-full w-full bg-sheet"
        >
          <ChatRouteSidebarListener />
          <DashboardSidebar />
          {isMobile && (
            <>
              <MobileChatHeader />
              <MobileSheet
                open={isChatSidebarOpen}
                onOpenChange={setIsChatSidebarOpen}
              >
                <ManageChats />
              </MobileSheet>
            </>
          )}

          <SidebarInset
            className={twMerge(
              isMobileLayoutRoute && isMobile ? "px-0 py-0 pt-14" : "py-3 pr-3",
              "bg-sheet h-full ",
            )}
          >
            {contentIsCard ? (
              <div className="max-sm:border-0 max-sm:rounded-none max-sm:shadow-none shadow-selectable border-0.5 border-border no-scrollbar rounded-[10px] bg-white overflow-y-scroll flex flex-col items-center h-full">
                <div
                  className={twMerge(
                    "overflow-y-scroll no-scrollbar flex flex-col h-full w-full",
                    isShopsHome
                      ? "items-stretch"
                      : "p-6 pb-0 max-w-[1440px] items-center max-sm:p-0",
                  )}
                >
                  <Outlet />
                  {children}
                </div>
              </div>
            ) : (
              <>
                <Outlet />
                {children}
              </>
            )}
          </SidebarInset>
        </SidebarProvider>
      </div>
    </div>
  );
};

export default DashboardLayout;
